import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { map, Observable } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';

import { capitalize, LOCATION, WINDOW } from '@personal-area/common';
import { Account } from '@personal-area/global/accounts/models';
import { EventType, GaEventClickDirective, GTMService } from '@personal-area/global/gtm';
import { PlatformsLinks, webTraderLinkSetup } from '@personal-area/global/platform-links';
import { ButtonComponent, TooltipCopyLinkComponent } from '@personal-area/global/ui';
import { IconSpriteModule } from '@personal-area/icon-sprite';

import { AccountViewerPasswordModule } from '../account-viewer-password/account-viewer-password.module';

export interface TradePopupModalData {
  account: Account;
}

export type AppType = 'desktop-app' | 'web-app';

@Component({
  selector: 'global-trade-popup',
  templateUrl: './trade-popup.component.html',
  styleUrls: ['./trade-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    TooltipCopyLinkComponent,
    ButtonComponent,
    QRCodeComponent,
    AccountViewerPasswordModule,
    RouterLink,
    IconSpriteModule,
    GaEventClickDirective,
    MatDialogClose,
    AsyncPipe,
    MatTooltip,
  ],
})
export class TradePopupComponent {
  private _gtmService = inject(GTMService);

  @Output() onChangeMetaTraderPswdClick = new EventEmitter<void>();

  public account = this.data.account;
  public server = this.account.server.type;
  public isMobile$: Observable<boolean>;
  public metaTraderQRUrl: string;
  readonly eventType = EventType;

  private _platformLinks = new PlatformsLinks();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TradePopupModalData,
    @Inject(LOCATION) private _location: Location,
    @Inject(WINDOW) private _window: Window,
    breakpointObserver: BreakpointObserver,
  ) {
    this.isMobile$ = breakpointObserver.observe(['(max-width: 767px)']).pipe(
      map(result => result.matches),
    );

    const url = new URL(`${this._location.origin}/cabinet/redirect/metatrader`);
    url.searchParams.append('login', this.account.login.toString());
    url.searchParams.append('server', this.account.server.name || '');
    url.searchParams.append('version', this.account.server.type || 'mt5');

    this.metaTraderQRUrl = url.toString();
  }

  public controlAppButtons(appType: AppType): void {
    switch (appType) {
      case 'desktop-app':
        this._location.href = this.server === 'mt4'
          ? this._platformLinks.mt4link
          : this._platformLinks.mt5link;
        break;
      case 'web-app':
        this._window.open(
          webTraderLinkSetup(this._platformLinks.links['webtrader'][this.server], this.account),
          '_blank',
        );
    }
  }

  public onCopy(name: string) {
    this._gtmService.event(EventType.InteractiveClick, {
      context: 'account_details',
      object: `mt5${capitalize(name)}Copy`,
      action: 'click',
      button_id: name,
    });
  }
}
